import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import api, { API, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Moon, Sun, LogOut, Award, Calendar, Clock, TrendingUp, GraduationCap, ExternalLink, User } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import NotificationBell from "@/components/NotificationBell";
import { DetailSkeleton } from "@/components/Skeletons";
import { fmtDate } from "@/lib/dates";
import { hhmm, joinMeta } from "@/lib/utils";

const EMPTY_STATS = {
  total_trainings: 0, total_hours: 0, certificates: 0, attendance_pct: 0, upcoming_count: 0,
};

function KPI({ icon: Icon, label, value, testid }) {
  return (
    <Card className="kpi-card p-5 border border-border" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="overline text-muted-foreground">{label}</div>
          <div className="font-heading font-bold text-3xl mt-2">{value}</div>
        </div>
        <div className="w-9 h-9 rounded-md bg-primary/10 text-primary flex items-center justify-center">
          <Icon className="w-4 h-4" aria-hidden="true" />
        </div>
      </div>
    </Card>
  );
}

export default function EmployeeDashboard() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  // Why the profile did not load, while none has been drawn yet. It carries the name, the
  // role and the password flag the whole page is built from, so with no profile there is
  // no section to put an error into: the page itself says so.
  const [profileError, setProfileError] = useState(null);
  // Whether a profile has been drawn. load is created once and reads this rather than the
  // state, because a refresh that fails behind a page already on screen is a toast, not a
  // reason to take the page away.
  const profileShown = useRef(false);
  // Numbered: four section Try agains, the profile's own and the reload after a password
  // change can all be out at once, and a slow failure landing after a newer success put
  // "Could not load your figures" back over figures that had just loaded.
  const loadSeq = useRef(0);
  // Every one of these calls is refused with a 403 until a first-login password is set,
  // and the catch below hands back an empty object. `${stats.attendance_pct}%` then
  // stringified undefined and the tile read "undefined%" over the password dialog.
  const [stats, setStats] = useState(EMPTY_STATS);
  const [trainings, setTrainings] = useState({ upcoming: [], past: [] });
  const [certs, setCerts] = useState([]);
  // Which of stats, trainings and certificates did not load. They still hold the empty
  // shape, so nothing reads `.upcoming` off null; this is what stops it reading as "none".
  const [failed, setFailed] = useState([]);
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });

  const load = useCallback(async () => {
    // Only the profile is required: it carries must_change_password, which decides
    // whether the dialog opens. Everything else is refused until that password is set,
    // so letting any of them reject would take down the page holding the way out.
    // That refusal, a 403, is the only honest empty. A 500, a 502 or a timeout got the
    // same empty shape with nothing to say so, and the portal told somebody holding
    // certificates that they had none, and how to earn one.
    const empty = (v, key) => (e) => (e.response?.status === 403 ? { data: v } : { data: v, failed: key });
    // A 200 that is not the shape asked for (an HTML error page) fails the same way.
    const shaped = (ok) => (r) => (ok(r.data) ? r : Promise.reject(new Error("unexpected response")));
    // Back to the skeleton while a retry is on its way, so it reads as loading rather than
    // as the same failure sitting still. Behind a profile already drawn this changes nothing.
    const seq = ++loadSeq.current;
    setProfileError(null);
    const [p, s, t, c] = await Promise.all([
      // Caught like the rest, though it is the one the page cannot do without. Left to
      // reject, a 500 kept the skeleton up for good, and the same failure during a later
      // Try again left load rejecting with nobody listening, so every retry button looked
      // dead. /me sits outside Protected, so nothing short of a total outage catches it
      // anywhere else. A 200 with no user in it fails here too, rather than taking the
      // header down reading a name that is not there.
      api.get("/my/profile").then(shaped((d) => !!d?.user && typeof d.user === "object"))
        .catch((e) => ({ error: { reason: formatApiError(e.response?.data?.detail, "") } })),
      api.get("/my/stats").then(shaped((d) => d !== null && typeof d === "object"))
        .catch(empty(EMPTY_STATS, "stats")),
      api.get("/my/trainings").then(shaped((d) => Array.isArray(d?.upcoming) && Array.isArray(d?.past)))
        .catch(empty({ upcoming: [], past: [] }, "trainings")),
      api.get("/my/certificates").then(shaped(Array.isArray)).catch(empty([], "certificates")),
    ]);
    if (seq !== loadSeq.current) return;
    setStats(s.data); setTrainings(t.data); setCerts(c.data);
    setFailed([s, t, c].map((x) => x.failed).filter(Boolean));
    if (p.error) {
      if (profileShown.current) toast.error(`Could not refresh your record.${p.error.reason ? ` ${p.error.reason}` : ""}`);
      else setProfileError(p.error);
      return;
    }
    profileShown.current = true;
    setProfile(p.data);
    if (p.data.user.must_change_password) setPwOpen(true);
  }, []);
  useEffect(() => { load(); }, [load]);

  const changePw = async () => {
    if (pw.next !== pw.confirm) { toast.error("Passwords don't match"); return; }
    if (pw.next.length < 6) { toast.error("Min 6 chars"); return; }
    try {
      // Changing a password outdates every token the account is holding. The replacement
      // rides back on the response so the person who just did it keeps the screen they
      // are standing on; anyone else signed in as them does not, which is the point.
      const r = await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      if (r.data?.token) localStorage.setItem("token", r.data.token);
      toast.success("Password updated");
      setPwOpen(false); setPw({ current: "", next: "", confirm: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const signOut = async () => { await logout(); nav("/login"); };

  if (!profile && profileError) {
    // Without a profile there is no header, and so no Sign out in it. This page is the only
    // way off a record that will not load, a disabled account included, where the reason
    // is the server's and another try changes nothing.
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md w-full p-8 border border-border" data-testid="my-profile-error">
          <div className="overline text-muted-foreground">My Portal</div>
          <h1 className="font-heading font-bold text-2xl mt-1">Could not load your record</h1>
          <p className="text-sm text-muted-foreground mt-2">
            {profileError.reason || "Abhyas did not send it back. It may be restarting, so try again in a moment."}
          </p>
          <div className="flex flex-wrap gap-2 mt-6">
            <Button onClick={load} data-testid="my-profile-retry">Try again</Button>
            <Button variant="outline" onClick={signOut}>
              <LogOut className="w-4 h-4 mr-1.5" /> Sign out
            </Button>
          </div>
        </Card>
      </div>
    );
  }
  if (!profile || !stats) return <div className="p-6"><DetailSkeleton testid="my-dashboard-skeleton" /></div>;

  const emp = profile.employee;
  const failedStats = failed.includes("stats");
  const failedTrainings = failed.includes("trainings");
  const failedCerts = failed.includes("certificates");
  // A section that did not load says so and offers another go, in place of the sentence
  // it shows when there is genuinely nothing.
  const couldNotLoad = (what, testid) => (
    <div className="text-sm" data-testid={testid}>
      <p className="text-destructive-text">Could not load your {what}.</p>
      <Button variant="outline" size="sm" className="mt-2" onClick={load}>Try again</Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="employee-dashboard">
      <header className="h-16 glass-header sticky top-0 z-30 flex items-center justify-between px-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-heading font-bold">A</div>
          <div>
            <div className="overline text-muted-foreground">My Portal</div>
            <div className="font-heading font-bold text-sm">Abhyas</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right hidden sm:block mr-2">
            <div className="text-sm font-medium">{profile.user.name}</div>
            <div className="text-xs overline text-primary">{profile.user.role}</div>
          </div>
          <NotificationBell />
              <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" aria-label="Toggle theme">
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setPwOpen(true)} data-testid="change-pw-btn">Change password</Button>
          <Button variant="outline" size="sm" onClick={signOut} data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-1.5" /> Sign out
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="overline text-primary">Welcome back</div>
            <h2 className="font-heading font-bold text-3xl mt-1">Hi, {profile.user.name.split(" ")[0]}</h2>
            {emp && <p className="text-sm text-muted-foreground mt-1">{emp.employee_code} · {emp.designation} · {emp.department_name} · {emp.unit_name}</p>}
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {/* A dash, not a zero, when the figures did not arrive: 0 is an answer. */}
          <KPI icon={GraduationCap} label="Trainings" value={failedStats ? "—" : stats.total_trainings} testid="emp-kpi-trainings" />
          <KPI icon={Clock} label="Total Hours" value={failedStats ? "—" : stats.total_hours} testid="emp-kpi-hours" />
          <KPI icon={Award} label="Certificates" value={failedStats ? "—" : stats.certificates} testid="emp-kpi-certs" />
          <KPI icon={TrendingUp} label="Attendance %" value={failedStats ? "—" : `${stats.attendance_pct}%`} testid="emp-kpi-att" />
        </div>
        {failedStats && couldNotLoad("figures", "emp-stats-error")}

        <Card className="p-6 border border-border" data-testid="emp-upcoming">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline text-muted-foreground">Upcoming</div>
              <h3 className="font-heading font-semibold text-lg mt-1">
                {failedTrainings ? "Nominations" : `Nominated for ${trainings.upcoming.length} training(s)`}
              </h3>
            </div>
            <Calendar className="w-5 h-5 text-primary" aria-hidden="true" />
          </div>
          {failedTrainings ? couldNotLoad("trainings", "emp-upcoming-error") : trainings.upcoming.length === 0 ? (
            <p className="text-sm text-muted-foreground">No upcoming trainings. HR will nominate you when scheduled.</p>
          ) : (
            <div className="space-y-2">
              {trainings.upcoming.map((t) => (
                <div key={t.id} className="flex items-start justify-between p-3 rounded-md border border-border hover:border-primary/40" data-testid={`upcoming-${t.id}`}>
                  <div>
                    <div className="font-medium">{t.title}</div>
                    {/* Joined and trimmed to minutes, as the training peek does. Raw, a training
                        saved without times read "25 Aug 2026 · – · Training Hall 1", and a
                        blank venue left a separator hanging off the end. */}
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {joinMeta(fmtDate(t.training_date), t.start_time && t.end_time && `${hhmm(t.start_time)}–${hhmm(t.end_time)}`, t.venue)}
                    </div>
                  </div>
                  <Badge variant="outline">{t.workflow_status}</Badge>
                </div>
              ))}
            </div>
          )}
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="p-6 border border-border" data-testid="emp-history">
            <div className="overline text-muted-foreground">My history</div>
            <h3 className="font-heading font-semibold text-lg mt-1 mb-3">Past trainings</h3>
            {failedTrainings ? couldNotLoad("trainings", "emp-history-error")
              : trainings.past.length === 0 && <p className="text-sm text-muted-foreground">No completed trainings yet.</p>}
            <div className="space-y-2 max-h-[420px] overflow-y-auto">
              {trainings.past.map((t) => (
                <div key={t.id} className="p-3 rounded-md border border-border">
                  <div className="flex justify-between">
                    <div className="font-medium">{t.title}</div>
                    <Badge variant={t.attendance_status === "Present" ? "default" : "outline"}>{t.attendance_status}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">{fmtDate(t.training_date)} · {t.duration_hours} hrs · {t.category}</div>
                  {/* No ?auth= on the certificate links. Only /attachments/:id/download reads
                      that parameter; this route authenticates from the cookie like everything
                      else, so appending the token put a 7-day bearer into the URL, the browser
                      history and any Referer, and bought nothing. */}
                  {t.certificate_number && (
                    <a href={`${API}/certificates/${t.participant_id}/pdf`} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1 mt-2 font-mono">
                      <ExternalLink className="w-3 h-3" /> {t.certificate_number}
                    </a>
                  )}
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-6 border border-border" data-testid="emp-certs">
            <div className="overline text-muted-foreground">My certificates</div>
            <h3 className="font-heading font-semibold text-lg mt-1 mb-3">{failedCerts ? "Certificates" : `${certs.length} certificate(s)`}</h3>
            {failedCerts ? couldNotLoad("certificates", "emp-certs-error")
              : certs.length === 0 && <p className="text-sm text-muted-foreground">Attend a training and get marked Present to earn certificates.</p>}
            <div className="space-y-2">
              {certs.map((c) => (
                <a key={c.participant_id} href={`${API}/certificates/${c.participant_id}/pdf`} target="_blank" rel="noreferrer" className="block p-3 rounded-md border border-border hover:border-primary/40" data-testid={`emp-cert-${c.participant_id}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium">{c.title}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{c.category} · {fmtDate(c.training_date)}</div>
                      <div className="text-xs font-mono text-primary mt-1">{c.certificate_number}</div>
                    </div>
                    <ExternalLink className="w-4 h-4 text-primary" />
                  </div>
                </a>
              ))}
            </div>
          </Card>
        </div>

      </main>

      {/* When the password must be set, the dialog is the whole screen: Cancel is already
          disabled, but Radix still closes on Escape and on a click outside, and one
          keypress left the portal usable on the default password — which is the
          employee_code, printed on a list every HR user can read. */}
      <Dialog open={pwOpen} onOpenChange={(v) => { if (!v && profile.user.must_change_password) return; setPwOpen(v); }}>
        <DialogContent
          data-testid="pw-dialog"
          onEscapeKeyDown={(e) => profile.user.must_change_password && e.preventDefault()}
          onPointerDownOutside={(e) => profile.user.must_change_password && e.preventDefault()}
          onInteractOutside={(e) => profile.user.must_change_password && e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>{profile.user.must_change_password ? "Set your password" : "Change password"}</DialogTitle>
            <DialogDescription>Use at least 6 characters. You'll stay logged in after updating.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Current password</Label><Input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="pw-current" /></div>
            <div><Label>New password</Label><Input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} data-testid="pw-new" /></div>
            <div><Label>Confirm new password</Label><Input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="pw-confirm" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwOpen(false)} disabled={profile.user.must_change_password}>Cancel</Button>
            <Button onClick={changePw} data-testid="pw-submit">Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
